import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "./api";
import { formatJpy, formatJpyWithUsd } from "./money";
import type {
  Batch,
  BatchConfig,
  BatchSummary,
  FeedBatch,
  GalleryImage,
  ImageItem,
  Meta,
  PlanResult,
  Preset,
  TagCount,
  UploadItem,
  UrlPreview,
  UseCaseTemplate,
} from "./types";
import { AspectPicker } from "./components/AspectPicker";
import { BatchCard } from "./components/BatchCard";
import { HistoryPanel } from "./components/HistoryPanel";
import { LoadMore } from "./components/LoadMore";
import { Lightbox } from "./components/Lightbox";
import { Login } from "./components/Login";
import { PresetBar } from "./components/PresetBar";
import { ReferenceUploader } from "./components/ReferenceUploader";
import { SnippetChips } from "./components/SnippetChips";
import { TemplatePicker } from "./components/TemplatePicker";
import { UrlSources } from "./components/UrlSources";
import { VariationPicker } from "./components/VariationPicker";
import { ResultGrid } from "./components/ResultGrid";
import { IconCopy, IconHistory, IconSparkle, IconStar, IconTag } from "./components/icons";
import { Segmented, Spinner, StepSection, Stepper, Toast, cx } from "./components/ui";

const STORAGE_KEY = "image-forge:config:v1";

const emptyConfig: BatchConfig = {
  title: "",
  prompt: "",
  style: "",
  negative: "",
  models: ["gpt-image-2"],
  aspects: ["1:1"],
  resolution: "standard",
  quality: "medium",
  outputFormat: "png",
  compression: 85,
  background: "auto",
  moderation: "auto",
  variantsPer: 4,
  variation: "same",
  references: [],
  inputFidelity: "high",
};

function loadStored(): { config: BatchConfig; uploads: UploadItem[] } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as { config: BatchConfig; uploads: UploadItem[] };
  } catch {
    return null;
  }
}

/** 所要時間の目安を「約3分」のような表記にする */
function fmtEta(seconds: number): string {
  if (seconds < 60) return `約${Math.max(10, Math.round(seconds / 10) * 10)}秒`;
  return `約${Math.round(seconds / 60)}分`;
}

export default function App() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [meta, setMeta] = useState<Meta | null>(null);
  const stored = useRef(loadStored());

  const [config, setConfig] = useState<BatchConfig>(stored.current?.config ?? emptyConfig);
  const [uploads, setUploads] = useState<UploadItem[]>(stored.current?.uploads ?? []);
  const [plan, setPlan] = useState<PlanResult | null>(null);

  // 1回の生成 = 1バッチ。フィードに新しい順で積む
  const [feed, setFeed] = useState<FeedBatch[]>([]);
  const [feedHasMore, setFeedHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [history, setHistory] = useState<BatchSummary[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [presets, setPresets] = useState<Preset[]>([]);
  const [lightbox, setLightbox] = useState<{ batchId?: string; gallery?: boolean; index: number } | null>(null);
  const [toasts, setToasts] = useState<{ id: number; text: string; tone: "ok" | "error" }[]>([]);

  // 横断ギャラリー（★のみ / タグ絞り込み）。fav か tags が立っている間はフィードの代わりに表示
  const [galleryFav, setGalleryFav] = useState(false);
  const [galleryTags, setGalleryTags] = useState<string[]>([]);
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [galleryTotal, setGalleryTotal] = useState(0);
  const [galleryHasMore, setGalleryHasMore] = useState(false);
  const [knownTags, setKnownTags] = useState<TagCount[]>([]);

  // プロンプト中の URL の読み込み結果
  const [urlSources, setUrlSources] = useState<UrlPreview[]>([]);
  const [urlLoading, setUrlLoading] = useState(false);
  // URL から取り込めた画像の候補（押すと参照画像に入る）
  const [urlImages, setUrlImages] = useState<UploadItem[]>([]);
  const galleryActive = galleryFav || galleryTags.length > 0;

  const esMap = useRef(new Map<string, EventSource>());
  const toastId = useRef(0);
  const formRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  // 監視とスクロールの両方から呼ばれるので、state ではなく ref で同期的に弾く
  const loadingRef = useRef(false);

  // loadMore を毎回作り直すと IntersectionObserver を張り直すことになるので、
  // 変化する値は ref 経由で読む
  const feedRef = useRef(feed);
  const galleryImagesRef = useRef(galleryImages);
  const galleryActiveRef = useRef(galleryActive);
  const galleryFavRef = useRef(galleryFav);
  const galleryTagsRef = useRef(galleryTags);
  feedRef.current = feed;
  galleryImagesRef.current = galleryImages;
  galleryActiveRef.current = galleryActive;
  galleryFavRef.current = galleryFav;
  galleryTagsRef.current = galleryTags;

  const toast = useCallback((text: string, tone: "ok" | "error" = "ok") => {
    const id = ++toastId.current;
    setToasts((t) => [...t, { id, text, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), tone === "error" ? 7000 : 3200);
  }, []);

  const patch = useCallback((p: Partial<BatchConfig>) => setConfig((c) => ({ ...c, ...p })), []);

  /* ------------------------------------------------------------ フィード操作 */

  const patchFeedImage = useCallback((image: ImageItem) => {
    setFeed((list) =>
      list.map((b) =>
        b.id === image.batch_id
          ? { ...b, images: b.images.map((i) => (i.id === image.id ? image : i)) }
          : b,
      ),
    );
    // ギャラリー側にも同じ画像がいれば反映する
    setGalleryImages((list) => list.map((i) => (i.id === image.id ? { ...i, ...image } : i)));
  }, []);

  const loadTags = useCallback(() => {
    void api
      .tags()
      .then((r) => setKnownTags(r.tags))
      .catch(() => {});
  }, []);

  const loadGallery = useCallback((fav: boolean, tags: string[]) => {
    void api
      .gallery({ favorite: fav, tags })
      .then((r) => {
        setGalleryImages(r.images);
        setGalleryTotal(r.total);
        setGalleryHasMore(r.hasMore);
      })
      .catch(() => {});
  }, []);

  /** フィード / ギャラリーの続きを読む（重複は id で弾く） */
  const loadMore = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoadingMore(true);
    try {
      if (galleryActiveRef.current) {
        const r = await api.gallery({
          favorite: galleryFavRef.current,
          tags: galleryTagsRef.current,
          offset: galleryImagesRef.current.length,
        });
        setGalleryImages((list) => {
          const seen = new Set(list.map((i) => i.id));
          return [...list, ...r.images.filter((i) => !seen.has(i.id))];
        });
        setGalleryHasMore(r.hasMore);
      } else {
        const oldest = feedRef.current[feedRef.current.length - 1];
        if (!oldest) return;
        const r = await api.feed({ before: oldest.created_at });
        setFeed((list) => {
          const seen = new Set(list.map((b) => b.id));
          return [...list, ...r.batches.filter((b) => !seen.has(b.id))];
        });
        setFeedHasMore(r.hasMore);
      }
    } catch {
      /* 次のスクロールで再試行される */
    } finally {
      loadingRef.current = false;
      setLoadingMore(false);
    }
  }, []);

  const patchFeedBatch = useCallback((batchId: string, p: Partial<FeedBatch>) => {
    setFeed((list) => list.map((b) => (b.id === batchId ? { ...b, ...p } : b)));
  }, []);

  const loadHistory = useCallback(() => {
    void api
      .batches()
      .then((r) => setHistory(r.batches))
      .catch(() => {});
  }, []);

  /* ------------------------------------------------------------- SSE 購読 */

  const subscribe = useCallback(
    (batchId: string) => {
      if (esMap.current.has(batchId)) return;
      const es = new EventSource(`/api/batches/${batchId}/stream`);
      esMap.current.set(batchId, es);
      es.onmessage = (ev) => {
        const data = JSON.parse(ev.data) as
          | { type: "image"; image: ImageItem }
          | { type: "batch"; status: Batch["status"]; done: number; total: number };
        if (data.type === "image") {
          patchFeedImage(data.image);
        } else {
          patchFeedBatch(batchId, { status: data.status, running: data.status === "running" });
          if (data.status !== "running") {
            es.close();
            esMap.current.delete(batchId);
            loadHistory();
          }
        }
      };
      es.onerror = () => {
        es.close();
        esMap.current.delete(batchId);
      };
    },
    [loadHistory, patchFeedBatch, patchFeedImage],
  );

  useEffect(() => {
    const map = esMap.current;
    return () => {
      for (const es of map.values()) es.close();
      map.clear();
    };
  }, []);

  /* ------------------------------------------------------------ 起動処理 */

  useEffect(() => {
    void api
      .session()
      .then((s) => setAuthed(s.authed))
      .catch(() => setAuthed(false));
  }, []);

  useEffect(() => {
    if (!authed) return;
    void api
      .meta()
      .then((m) => {
        setMeta(m);
        setConfig((c) => {
          const valid = c.models.filter((id) => m.models.some((x) => x.id === id));
          return valid.length === c.models.length ? c : { ...c, models: valid.length ? valid : [m.defaults.model] };
        });
      })
      .catch(() => toast("設定の読み込みに失敗しました", "error"));
    void api.presets().then((r) => setPresets(r.presets)).catch(() => {});
    loadHistory();
    loadTags();
    // 直近のバッチをフィードに復元し、走っているものは購読し直す
    void api
      .feed()
      .then((r) => {
        setFeed(r.batches);
        setFeedHasMore(r.hasMore);
        for (const b of r.batches) if (b.running) subscribe(b.id);
      })
      .catch(() => {});
  }, [authed, loadHistory, loadTags, subscribe, toast]);

  // プロンプト/スタイルに URL が現れたら読みに行く（URL の集合が変わったときだけ）
  const urlKey = useMemo(() => {
    const found = `${config.prompt}\n${config.style}`.match(/https?:\/\/[^\s<>"'）」』]+/g) ?? [];
    return [...new Set(found.map((u) => u.replace(/[.,、。)】]+$/, "")))].slice(0, 4).join("\n");
  }, [config.prompt, config.style]);

  useEffect(() => {
    if (!authed) return;
    if (!urlKey) {
      setUrlSources([]);
      setUrlLoading(false);
      return;
    }
    const ctrl = new AbortController();
    setUrlLoading(true);
    const timer = setTimeout(() => {
      void api
        .urlPreview(urlKey, ctrl.signal)
        .then((r) => {
          setUrlSources(r.sources);
          // 参照画像にすると生成が i2i（元画像の加工）に変わってしまうので、
          // 勝手には足さず候補として出すだけにする
          setUrlImages(r.uploads);
        })
        .catch(() => {})
        .finally(() => setUrlLoading(false));
    }, 600);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [authed, urlKey]);

  // 一番下の番兵が見えたら続きを読む（無限スクロール）
  const canLoadMore = galleryActive ? galleryHasMore : feedHasMore;
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !canLoadMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) void loadMore();
      },
      // スクロールしているのは main 要素なので、画面ではなくそれを基準にする。
      // 下端に到達する少し手前で読み始める。
      { root: mainRef.current, rootMargin: "600px" },
    );
    io.observe(el);

    // タブが非表示だと IntersectionObserver はコールバックを配送しないため、
    // スクロール位置での判定も併用する（loadMore 側で二重実行は防いでいる）
    const root = mainRef.current;
    const onScroll = () => {
      if (!root) return;
      if (root.scrollTop + root.clientHeight >= root.scrollHeight - 600) void loadMore();
    };
    root?.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      io.disconnect();
      root?.removeEventListener("scroll", onScroll);
    };
  }, [canLoadMore, loadMore]);

  // フィルタが変わったらギャラリーを引き直す
  useEffect(() => {
    if (!authed || !galleryActive) return;
    loadGallery(galleryFav, galleryTags);
  }, [authed, galleryActive, galleryFav, galleryTags, loadGallery]);

  /* ------------------------------------------------ 設定の保存 & プレビュー */

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ config, uploads }));
  }, [config, uploads]);

  const configWithRefs = useMemo<BatchConfig>(
    () => ({ ...config, references: uploads.map((u) => u.id) }),
    [config, uploads],
  );

  useEffect(() => {
    if (!authed) return;
    const ctrl = new AbortController();
    const timer = setTimeout(() => {
      void api
        .plan(configWithRefs, ctrl.signal)
        .then(setPlan)
        .catch(() => {});
    }, 150);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [authed, configWithRefs]);

  /* ---------------------------------------------------------------- 生成 */

  const startBatch = useCallback(
    async (cfg: BatchConfig, ignoreFailedUrls = false) => {
      const res = await api.createBatch(cfg, ignoreFailedUrls);
      setFeed((list) => [{ ...res.batch, images: res.images, running: true }, ...list]);
      subscribe(res.batch.id);
      loadHistory();
    },
    [loadHistory, subscribe],
  );

  const generate = useCallback(async () => {
    if (submitting) return;
    if (!config.prompt.trim()) {
      toast("プロンプトを入力してください", "error");
      return;
    }
    // URL が読めていないまま生成すると、URL を見ていない画像が出来上がる。
    // 黙って進めず、ここで気づけるようにする。
    const failedUrls = urlSources.filter((u) => !u.ok);
    if (failedUrls.length > 0) {
      const detail = failedUrls
        .map((f) => "・" + f.url + "\n　" + (f.error ?? "読み込み失敗"))
        .join("\n");
      const ok = window.confirm(
        "次のURLを読み込めていません。\n\n" +
          detail +
          "\n\nURLの内容を反映しないまま生成しますか？",
      );
      if (!ok) return;
    }

    setSubmitting(true);
    try {
      await startBatch(configWithRefs, failedUrls.length > 0);
      // 続けて次の指示を出しやすいよう、フォームはそのまま残す
      toast("生成を開始しました。設定を変えてすぐ次を実行できます");
    } catch (err) {
      toast(err instanceof Error ? err.message : "生成の開始に失敗しました", "error");
    } finally {
      setSubmitting(false);
    }
  }, [config.prompt, configWithRefs, startBatch, submitting, toast, urlSources]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        void generate();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [generate]);

  /* ------------------------------------------------------------ 各種操作 */

  const applyTemplate = useCallback(
    (t: UseCaseTemplate) => {
      patch({
        prompt: t.prompt,
        style: t.style,
        negative: t.negative,
        aspects: t.aspects,
        quality: t.quality,
        variantsPer: t.variantsPer,
        variation: "same",
        title: "",
      });
      formRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      toast(`テンプレート「${t.label}」を読み込みました。【】の中を書き換えてください`);
    },
    [patch, toast],
  );

  const insertSnippet = useCallback(
    (target: "prompt" | "style", text: string) => {
      setConfig((c) => {
        const cur = c[target].trim();
        return { ...c, [target]: cur ? `${cur}、${text}` : text };
      });
    },
    [],
  );

  const quote = useCallback(
    async (image: ImageItem) => {
      try {
        const res = await api.useAsReference(image.id);
        setUploads((list) => {
          if (list.some((u) => u.id === res.upload.id)) return list;
          return [...list, res.upload].slice(0, 8);
        });
        setLightbox(null);
        formRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        toast("参照画像に追加しました。プロンプトを変えて生成すると、この画像をベースに作ります");
      } catch (err) {
        toast(err instanceof Error ? err.message : "引用に失敗しました", "error");
      }
    },
    [toast],
  );

  const rerun = useCallback(
    async (b: FeedBatch) => {
      try {
        await startBatch(b.config);
        toast("同じ設定でもう1バッチ開始しました");
      } catch (err) {
        toast(err instanceof Error ? err.message : "再生成に失敗しました", "error");
      }
    },
    [startBatch, toast],
  );

  const extend = useCallback(
    async (b: FeedBatch, n: number) => {
      try {
        const res = await api.extendBatch(b.id, n);
        patchFeedBatch(b.id, { images: res.images, status: "running", running: true });
        subscribe(b.id);
        toast(`${res.added}枚を追加生成します`);
      } catch (err) {
        toast(err instanceof Error ? err.message : "追加生成に失敗しました", "error");
      }
    },
    [patchFeedBatch, subscribe, toast],
  );

  const retryFailed = useCallback(
    async (b: FeedBatch) => {
      try {
        const r = await api.retryFailed(b.id);
        if (r.retried > 0) {
          patchFeedBatch(b.id, {
            status: "running",
            running: true,
            images: b.images.map((i) => (i.status === "error" ? { ...i, status: "pending", error: null } : i)),
          });
          subscribe(b.id);
          toast(`${r.retried}枚を再生成します`);
        }
      } catch (err) {
        toast(err instanceof Error ? err.message : "再生成に失敗しました", "error");
      }
    },
    [patchFeedBatch, subscribe, toast],
  );

  const restore = useCallback(
    (cfg: BatchConfig) => {
      setConfig({ ...emptyConfig, ...cfg, references: [] });
      setHistoryOpen(false);
      formRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      toast("設定をフォームに読み込みました");
    },
    [toast],
  );

  const copyPrompt = useCallback(
    (b: Batch) => {
      const parts = [b.prompt];
      if (b.style) parts.push(`\n【スタイル】\n${b.style}`);
      if (b.negative) parts.push(`\n【避けること】\n${b.negative}`);
      void navigator.clipboard.writeText(parts.join("\n"));
      toast("プロンプト一式をコピーしました");
    },
    [toast],
  );

  const toggleFavorite = useCallback(
    async (image: ImageItem) => {
      patchFeedImage({ ...image, favorite: !image.favorite });
      try {
        await api.setFavorite(image.id, !image.favorite);
        // ★のみ表示中に★を外したら一覧から消す
        if (image.favorite && galleryFav) {
          setGalleryImages((list) => list.filter((i) => i.id !== image.id));
          setGalleryTotal((n) => Math.max(0, n - 1));
        }
      } catch {
        patchFeedImage(image);
      }
    },
    [galleryFav, patchFeedImage],
  );

  const saveTags = useCallback(
    async (image: ImageItem, tags: string[]) => {
      const prev = image.tags;
      patchFeedImage({ ...image, tags });
      try {
        const r = await api.updateImage(image.id, { tags });
        patchFeedImage(r.image);
        loadTags();
        // タグ絞り込み中に条件から外れたら一覧から消す
        if (galleryTags.length > 0 && !galleryTags.every((t) => tags.includes(t))) {
          setGalleryImages((list) => list.filter((i) => i.id !== image.id));
          setGalleryTotal((n) => Math.max(0, n - 1));
        }
      } catch (err) {
        patchFeedImage({ ...image, tags: prev });
        toast(err instanceof Error ? err.message : "タグの保存に失敗しました", "error");
      }
    },
    [galleryTags, loadTags, patchFeedImage, toast],
  );

  const deleteImage = useCallback(
    async (image: ImageItem) => {
      try {
        await api.deleteImage(image.id);
        setFeed((list) =>
          list.map((b) =>
            b.id === image.batch_id ? { ...b, images: b.images.filter((i) => i.id !== image.id) } : b,
          ),
        );
      } catch (err) {
        toast(err instanceof Error ? err.message : "削除に失敗しました", "error");
      }
    },
    [toast],
  );

  const deleteBatch = useCallback(
    async (b: FeedBatch) => {
      try {
        await api.deleteBatch(b.id);
        setFeed((list) => list.filter((x) => x.id !== b.id));
        setHistory((list) => list.filter((x) => x.id !== b.id));
      } catch (err) {
        toast(err instanceof Error ? err.message : "削除に失敗しました", "error");
      }
    },
    [toast],
  );

  const openFromHistory = useCallback(
    async (id: string) => {
      try {
        const res = await api.batch(id);
        setFeed((list) => {
          if (list.some((b) => b.id === id)) return list;
          return [{ ...res.batch, images: res.images, running: res.running }, ...list];
        });
        if (res.running) subscribe(id);
        setHistoryOpen(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch {
        toast("履歴の読み込みに失敗しました", "error");
      }
    },
    [subscribe, toast],
  );

  /* --------------------------------------------------------------- 集計値 */

  // 選択中のモデルが共通して持つ能力だけを画面に出す
  const selectedModels = useMemo(
    () => (meta?.models ?? []).filter((m) => config.models.includes(m.id)),
    [meta, config.models],
  );
  /** 全モデルが受け付ける quality だけを選べるようにする（2.5 のみの xhigh/max を誤って送らない） */
  const availableQualities = useMemo(() => {
    const all = meta?.qualities ?? [];
    if (selectedModels.length === 0) return all;
    return all.filter((q) => selectedModels.every((m) => m.qualities.includes(q.id)));
  }, [meta, selectedModels]);
  const anyBackground = selectedModels.some((m) => m.supportsBackground);

  const modelLabels = useMemo(() => {
    const map: Record<string, string> = {};
    for (const m of meta?.models ?? []) map[m.id] = m.label;
    return map;
  }, [meta]);

  const runningCount = feed.filter((b) => b.status === "running").length;

  const lightboxImages = useMemo(() => {
    if (!lightbox) return [];
    if (lightbox.gallery) return galleryImages.filter((i) => i.status === "done");
    return feed.find((b) => b.id === lightbox.batchId)?.images.filter((i) => i.status === "done") ?? [];
  }, [feed, galleryImages, lightbox]);

  /* ---------------------------------------------------------------- 描画 */

  if (authed === null) {
    return (
      <div className="grid min-h-dvh place-items-center text-slate-400">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }
  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;

  const toggleIn = (list: string[], id: string) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  return (
    <div className="flex min-h-dvh flex-col">
      {/* ------------------------------------------------------------ ヘッダ */}
      <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-slate-200 bg-white/85 px-5 py-2.5 backdrop-blur">
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-brand-500 text-white">
          <IconSparkle className="h-[18px] w-[18px]" />
        </span>
        <div className="leading-tight">
          <h1 className="text-[16px] font-bold text-slate-800">Image Forge</h1>
          <p className="text-[12px] text-slate-400">同じ指示で一気に案出し</p>
        </div>

        {meta && (
          <span className="hidden rounded-lg bg-brand-50 px-2.5 py-1 text-[12px] font-bold text-brand-700 sm:inline">
            GPT Image 2
          </span>
        )}
        {runningCount > 0 && (
          <span className="flex items-center gap-1.5 rounded-lg bg-brand-100 px-2.5 py-1 text-[12px] font-bold text-brand-700">
            <Spinner className="h-3.5 w-3.5" />
            {runningCount}バッチ生成中
          </span>
        )}
        {meta && !meta.apiKeyConfigured && (
          <span className="rounded-lg bg-amber-100 px-2.5 py-1 text-[12px] font-semibold text-amber-700">
            OPENAI_API_KEY 未設定
          </span>
        )}

        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={() => setHistoryOpen(true)} className="btn-ghost">
            <IconHistory className="h-4 w-4" />
            履歴
          </button>
          <button
            type="button"
            onClick={() => {
              void api.logout().then(() => setAuthed(false));
            }}
            className="btn-ghost text-slate-400"
          >
            ログアウト
          </button>
        </div>
      </header>

      <div className="flex flex-1 flex-col lg:flex-row">
        {/* ------------------------------------------------------- 設定パネル */}
        <div
          ref={formRef}
          className="thin-scroll w-full shrink-0 border-b border-slate-200 bg-white lg:h-[calc(100dvh-57px)] lg:w-[560px] lg:overflow-y-auto lg:border-b-0 lg:border-r"
        >
          <div className="border-b border-slate-100 px-5 py-3">
            <div className="mb-1.5 text-[12.5px] font-bold text-slate-400">テンプレートから始める</div>
            {meta && <TemplatePicker templates={meta.templates} compact onApply={applyTemplate} />}
          </div>

          <PresetBar
            presets={presets}
            onApply={(p) => {
              restore(p.config);
              void api.updatePreset(p.id, { touch: true }).catch(() => {});
            }}
            onSave={(name) => {
              void api
                .createPreset(name, "", configWithRefs)
                .then((r) => {
                  setPresets((list) => [r.preset, ...list]);
                  toast("プリセットを保存しました");
                })
                .catch(() => toast("保存に失敗しました", "error"));
            }}
            onTogglePin={(p) => {
              void api.updatePreset(p.id, { pinned: !p.pinned }).then((r) => {
                setPresets((list) => list.map((x) => (x.id === p.id ? r.preset : x)));
              });
            }}
            onDelete={(p) => {
              void api.deletePreset(p.id).then(() => setPresets((list) => list.filter((x) => x.id !== p.id)));
            }}
          />

          <StepSection
            step={1}
            title="プロンプト"
            sub="何を作るか"
            aside={
              config.prompt ? (
                <button
                  type="button"
                  className="chip"
                  onClick={() => {
                    void navigator.clipboard.writeText(config.prompt);
                    toast("コピーしました");
                  }}
                >
                  <IconCopy className="h-3.5 w-3.5" />
                  コピー
                </button>
              ) : null
            }
          >
            <textarea
              value={config.prompt}
              onChange={(e) => patch({ prompt: e.target.value })}
              rows={5}
              placeholder="例：新発売のいちごミルクを持つ20代女性。明るい店内、自然光、手元にフォーカス。商品パッケージがはっきり見えるように。"
              className="field resize-y"
            />
            <input
              value={config.title}
              onChange={(e) => patch({ title: e.target.value })}
              placeholder="この案件のメモ（一覧での見出しになります・任意）"
              className="field mt-2 py-2"
            />
            <p className="mt-1.5 text-[12px] text-slate-400">
              URL を書くと、そのページの内容を読み取って生成の参考にします
            </p>
            <UrlSources
              sources={urlSources}
              loading={urlLoading}
              images={urlImages}
              attachedIds={uploads.map((u) => u.id)}
              onToggleImage={(img) =>
                setUploads((list) =>
                  list.some((u) => u.id === img.id)
                    ? list.filter((u) => u.id !== img.id)
                    : [...list, img].slice(0, 8),
                )
              }
            />
          </StepSection>

          <div className="border-t border-slate-100" />

          <StepSection
            step={2}
            title="バリエーション戦略"
            sub="案をどう散らすか"
          >
            {meta && (
              <VariationPicker
                strategies={meta.variations}
                selected={config.variation}
                variantsPer={config.variantsPer}
                onSelect={(s) => {
                  if (s.axes.length > 0) {
                    patch({ variation: s.id, variantsPer: s.axes.length });
                    toast(`${s.axes.length}案（全軸1つずつ）に設定しました。枚数は変更できます`);
                  } else {
                    patch({ variation: s.id });
                  }
                }}
              />
            )}
          </StepSection>

          <div className="border-t border-slate-100" />

          <StepSection step={3} title="スタイル・避けること" sub="任意">
            <div className="space-y-2">
              <textarea
                value={config.style}
                onChange={(e) => patch({ style: e.target.value })}
                rows={2}
                placeholder="スタイル・トーン：写真風／やわらかい配色／余白多め など"
                className="field resize-y"
              />
              <textarea
                value={config.negative}
                onChange={(e) => patch({ negative: e.target.value })}
                rows={2}
                placeholder="避けること：手指の破綻／過度な加工／競合ロゴ など"
                className="field resize-y"
              />
              {meta && (
                <SnippetChips
                  groups={meta.snippets}
                  currentText={{ prompt: config.prompt, style: config.style }}
                  onInsert={insertSnippet}
                />
              )}
            </div>
          </StepSection>

          <div className="border-t border-slate-100" />

          <StepSection
            step={4}
            title="参照画像"
            sub="任意 / 最大8枚 — 生成結果の🖼ボタンからも引用できます"
          >
            <ReferenceUploader items={uploads} onChange={setUploads} onError={(m) => toast(m, "error")} />
          </StepSection>

          <div className="border-t border-slate-100" />

          <StepSection step={5} title="アスペクト比" sub="複数選択可">
            <AspectPicker
              aspects={meta?.aspects ?? []}
              sets={meta?.aspectSets ?? []}
              selected={config.aspects}
              resolution={config.resolution}
              showPixels
              onToggle={(id) => patch({ aspects: toggleIn(config.aspects, id) })}
              onApplySet={(ids) => patch({ aspects: ids })}
            />
          </StepSection>

          <div className="border-t border-slate-100" />

          <StepSection step={6} title="枚数" sub="1つの比につき何案つくるか">
            <Stepper
              value={config.variantsPer}
              onChange={(v) => patch({ variantsPer: v })}
              min={1}
              max={meta?.limits.maxBatchSize ?? 60}
              quick={[1, 2, 4, 6, 10, 20]}
            />
            <p className="mt-2.5 text-[12.5px] text-slate-500">
              比 {config.aspects.length} × {config.variantsPer}案 ={" "}
              <span className="font-bold text-slate-700">{plan?.total ?? 0}枚</span>
            </p>
          </StepSection>

          <div className="border-t border-slate-100" />

          <StepSection step={7} title="出力設定">
            <div className="space-y-3.5">
              <div>
                <Segmented
                  title="品質"
                  value={config.quality}
                  options={availableQualities}
                  onChange={(v) => patch({ quality: v })}
                />
                {(config.quality === "xhigh" || config.quality === "max") && (
                  <p className="mt-1.5 text-[12px] font-semibold text-amber-600">
                    1枚あたりの時間と費用が大きく伸びます。枚数を絞って使ってください
                  </p>
                )}
              </div>
              <Segmented
                title="解像度"
                value={config.resolution}
                options={meta?.resolutionTiers ?? []}
                onChange={(v) => patch({ resolution: v })}
              />
              <Segmented
                title="ファイル形式"
                value={config.outputFormat}
                options={meta?.outputFormats ?? []}
                onChange={(v) => patch({ outputFormat: v })}
              />
              {anyBackground && (
                <div>
                  <Segmented
                    title="背景"
                    value={config.background}
                    options={[
                      { id: "auto", label: "自動" },
                      { id: "opaque", label: "不透過" },
                      { id: "transparent", label: "透過" },
                    ]}
                    onChange={(v) => {
                      // 透過は PNG / WebP でしか出せない
                      if (v === "transparent" && config.outputFormat === "jpeg") {
                        patch({ background: v, outputFormat: "png" });
                        toast("透過はJPEGで出せないため、PNGに切り替えました");
                      } else {
                        patch({ background: v });
                      }
                    }}
                  />
                  {config.background === "transparent" && (
                    <p className="mt-1.5 text-[12px] text-slate-400">
                      切り抜き素材向け。背景のないPNG / WebPで出力します
                    </p>
                  )}
                </div>
              )}
              {config.outputFormat !== "png" && (
                <label className="block">
                  <span className="mb-1.5 block text-[12.5px] font-semibold text-slate-500">
                    圧縮率 <span className="tabular-nums text-slate-400">{config.compression}</span>
                  </span>
                  <input
                    type="range"
                    min={20}
                    max={100}
                    value={config.compression}
                    onChange={(e) => patch({ compression: Number(e.target.value) })}
                    className="w-full accent-brand-500"
                  />
                </label>
              )}
            </div>
          </StepSection>

          {/* ----------------------------------------------------- 実行バー */}
          <div className="sticky bottom-0 flex flex-wrap items-center gap-3 border-t border-slate-200 bg-white/95 px-5 py-3 backdrop-blur">
            <div className="leading-tight">
              <div className="flex items-baseline gap-1.5">
                <span className="text-[24px] font-bold tabular-nums text-slate-800">{plan?.total ?? 0}</span>
                <span className="text-[14px] font-semibold text-slate-600">枚を生成</span>
              </div>
              <div className="mt-0.5 text-[12.5px] text-slate-400">
                <span title={plan ? formatJpyWithUsd(plan.estimatedCostUsd) : undefined}>
                  概算 {formatJpy(plan?.estimatedCostUsd ?? 0)}
                </span>
                {plan && plan.estimatedSeconds > 0 && <> ・ {fmtEta(plan.estimatedSeconds)}</>}
                {plan?.overLimit && (
                  <span className="ml-1.5 font-bold text-rose-500">上限 {plan.maxBatchSize} 枚超</span>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={() => void generate()}
              disabled={submitting || !plan || plan.total === 0 || plan.overLimit}
              className="btn-primary ml-auto px-7 py-3 text-[15px]"
              title="生成中でも続けて実行できます"
            >
              {submitting ? <Spinner className="h-[18px] w-[18px]" /> : <IconSparkle className="h-[18px] w-[18px]" />}
              生成する
              <kbd className="ml-1 rounded border border-white/30 px-1.5 py-0.5 text-[11.5px] font-semibold opacity-80">
                Ctrl+↵
              </kbd>
            </button>
          </div>
        </div>

        {/* ------------------------------------------------------- フィード */}
        <main
          ref={mainRef}
          className="thin-scroll flex-1 overflow-y-auto bg-slate-50 lg:h-[calc(100dvh-57px)]"
        >
          {/* ------------------------------------------------ フィルタバー */}
          <div className="sticky top-0 z-20 flex flex-wrap items-center gap-1.5 border-b border-slate-200 bg-white/90 px-4 py-2 backdrop-blur">
            <button
              type="button"
              onClick={() => {
                setGalleryFav(false);
                setGalleryTags([]);
              }}
              className={cx(
                "chip",
                !galleryActive && "border-brand-400 bg-brand-50 font-bold text-brand-700",
              )}
            >
              すべて
            </button>
            <button
              type="button"
              onClick={() => setGalleryFav((v) => !v)}
              className={cx(
                "chip",
                galleryFav && "border-amber-400 bg-amber-50 font-bold text-amber-700",
              )}
              title="★を付けた画像だけを全バッチ横断で表示"
            >
              <IconStar className="h-3.5 w-3.5" filled={galleryFav} />
              お気に入り
            </button>
            {knownTags.length > 0 && <span className="mx-1 h-4 w-px bg-slate-200" />}
            {knownTags.map((t) => {
              const on = galleryTags.includes(t.tag);
              return (
                <button
                  key={t.tag}
                  type="button"
                  onClick={() =>
                    setGalleryTags((list) => (on ? list.filter((x) => x !== t.tag) : [...list, t.tag]))
                  }
                  className={cx("chip", on && "border-brand-400 bg-brand-50 font-bold text-brand-700")}
                  title={`タグ「${t.tag}」で絞り込み（${t.count}枚）`}
                >
                  <IconTag className="h-3 w-3" />
                  {t.tag}
                  <span className="text-[11px] tabular-nums text-slate-400">{t.count}</span>
                </button>
              );
            })}
            {galleryActive && (
              <span className="ml-auto text-[12.5px] font-semibold text-slate-400">
                {galleryTotal}枚ヒット
              </span>
            )}
          </div>

          {galleryActive ? (
            /* -------------------------------------------- 横断ギャラリー */
            galleryImages.length === 0 ? (
              <div className="grid place-items-center p-16 text-center">
                <div>
                  <p className="text-[14px] font-bold text-slate-500">該当する画像がありません</p>
                  <p className="mt-1.5 text-[12.5px] text-slate-400">
                    画像にカーソルを合わせて ★ や 🏷 を付けると、ここで横断的に見られます
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-4">
                <ResultGrid
                  images={galleryImages}
                  groupByModel={false}
                  modelLabels={modelLabels}
                  knownTags={knownTags.map((t) => t.tag)}
                  hoverTitle={(img) => {
                    const g = img as GalleryImage;
                    return g.batch_title || g.batch_prompt;
                  }}
                  onOpen={(img) => {
                    const done = galleryImages.filter((i) => i.status === "done");
                    const idx = done.findIndex((i) => i.id === img.id);
                    if (idx >= 0) setLightbox({ gallery: true, index: idx });
                  }}
                  onToggleFavorite={(img) => void toggleFavorite(img)}
                  onQuote={(img) => void quote(img)}
                  onSaveTags={(img, tags) => void saveTags(img, tags)}
                />
                <LoadMore
                  ref={sentinelRef}
                  hasMore={galleryHasMore}
                  loading={loadingMore}
                  onClick={() => void loadMore()}
                  doneLabel={`${galleryImages.length}枚すべて表示しました`}
                />
              </div>
            )
          ) : feed.length === 0 ? (
            <div className="grid h-full place-items-center p-6">
              <div className="flex flex-col items-center text-center">
                <div className="mb-3.5 grid h-14 w-14 place-items-center rounded-2xl bg-brand-100 text-brand-500">
                  <IconSparkle className="h-7 w-7" />
                </div>
                <p className="text-[15px] font-bold text-slate-600">まずはテンプレートから</p>
                <p className="mb-5 mt-1.5 text-[13px] leading-relaxed text-slate-400">
                  そのまま生成して、気に入った1枚を「引用」して詰めていくのが速いです
                </p>
                {meta && <TemplatePicker templates={meta.templates} onApply={applyTemplate} />}
              </div>
            </div>
          ) : (
            <div className="space-y-4 p-4">
              {feed.map((b) => (
                <BatchCard
                  key={b.id}
                  batch={b}
                  variationLabel={
                    b.config.variation && b.config.variation !== "same"
                      ? meta?.variations.find((v) => v.id === b.config.variation)?.label
                      : undefined
                  }
                  modelLabels={modelLabels}
                  onOpenImage={(img) => {
                    const done = b.images.filter((i) => i.status === "done");
                    const idx = done.findIndex((i) => i.id === img.id);
                    if (idx >= 0) setLightbox({ batchId: b.id, index: idx });
                  }}
                  onToggleFavorite={(img) => void toggleFavorite(img)}
                  onQuote={(img) => void quote(img)}
                  onDeleteImage={(img) => void deleteImage(img)}
                  onRerun={() => void rerun(b)}
                  onExtend={(n) => void extend(b, n)}
                  onRestore={() => restore(b.config)}
                  onRetryFailed={() => void retryFailed(b)}
                  onCancel={() => {
                    void api.cancelBatch(b.id).then(() => toast("停止しました"));
                  }}
                  onDelete={() => void deleteBatch(b)}
                  onCopyPrompt={() => copyPrompt(b)}
                  knownTags={knownTags.map((t) => t.tag)}
                  onSaveTags={(img, tags) => void saveTags(img, tags)}
                />
              ))}
              <LoadMore
                ref={sentinelRef}
                hasMore={feedHasMore}
                loading={loadingMore}
                onClick={() => void loadMore()}
                doneLabel="これ以上前の生成はありません"
              />
            </div>
          )}
        </main>
      </div>

      <HistoryPanel
        open={historyOpen}
        batches={history}
        activeId={undefined}
        onOpen={(b) => void openFromHistory(b.id)}
        onRestore={(b) => restore(b.config)}
        onDelete={(b) => {
          void api.deleteBatch(b.id).then(() => {
            setHistory((list) => list.filter((x) => x.id !== b.id));
            setFeed((list) => list.filter((x) => x.id !== b.id));
          });
        }}
        onClose={() => setHistoryOpen(false)}
      />

      {lightbox !== null && lightboxImages.length > 0 && (
        <Lightbox
          images={lightboxImages}
          index={Math.min(lightbox.index, lightboxImages.length - 1)}
          modelLabels={modelLabels}
          onIndex={(i) => setLightbox({ ...lightbox, index: i })}
          onClose={() => setLightbox(null)}
          onToggleFavorite={(img) => void toggleFavorite(img)}
          onQuote={(img) => void quote(img)}
        />
      )}

      <Toast toasts={toasts} onDismiss={(id) => setToasts((t) => t.filter((x) => x.id !== id))} />
    </div>
  );
}
